import { useEffect, useMemo, useState } from "react";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";

import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

/**
 * Cadastro de novo registro em centro_custo_folga.
 * A obra deve ser escolhida entre os centros de custo ATIVOS (GetObrasAtivas / TOTVS),
 * igual ao fluxo original — sem digitar nome livre.
 */
const ModalNovoCentroCusto = ({ onSaved, obrasJaCadastradas = [] }) => {

    const api = useApi();
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();

    const [obra, setObra] = useState("");
    const [diasPrazo, setDiasPrazo] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [loadingObras, setLoadingObras] = useState(true);
    const [obrasAtivas, setObrasAtivas] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoadingObras(true);
            try {
                const { data } = await api.get("/Painel/API/GetObrasAtivas/");
                if (cancelled) return;
                setObrasAtivas(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!cancelled) {
                    console.error(e);
                    notifyError("Não foi possível carregar os centros de custo ativos.");
                    setObrasAtivas([]);
                }
            } finally {
                if (!cancelled) setLoadingObras(false);
            }
        };

        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectOptions = useMemo(() => {
        const cad = new Set((obrasJaCadastradas || []).filter(Boolean));
        return (obrasAtivas || [])
            .map((row) => (row && typeof row === "object" ? row.OBRA : row))
            .filter((nome) => typeof nome === "string" && nome.trim() !== "")
            .filter((nome) => !cad.has(nome))
            .sort((a, b) => a.localeCompare(b, "pt-BR"))
            .map((nome) => ({ value: nome, label: nome }));
    }, [obrasAtivas, obrasJaCadastradas]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const prazoNum = parseInt(diasPrazo, 10);
        if (!obra) {
            notifyError("Selecione o centro de custo.");
            return;
        }
        if (!Number.isFinite(prazoNum) || prazoNum <= 0) {
            notifyError("Informe um prazo válido (número de dias).");
            return;
        }

        setSubmitting(true);
        try {
            await api.post("/Painel/API/FolgasCampo/CentroCustoFolga/Save/index.php", {
                obra,
                dias_prazo: prazoNum,
            });
            notifySuccess("Centro de custo cadastrado.");
            onSaved?.();
            closeModal();
        } catch (err) {
            notifyError(err?.response?.data?.message || "Erro ao salvar.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Overlay state>
            <Modal size="md" header close={closeModal}>
                <Formulario onSubmit={handleSubmit}>
                    <FormComp
                        title="CENTRO DE CUSTO (OBRA)"
                        type="select"
                        obj
                        options={selectOptions}
                        valor={obra}
                        changeFunc={(e) => setObra(e.target.value)}
                        required
                        disabled={loadingObras}
                    />
                    {loadingObras && (
                        <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#6b7280" }}>
                            Carregando centros de custo ativos…
                        </p>
                    )}
                    {!loadingObras && selectOptions.length === 0 && (
                        <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#b45309" }}>
                            Não há centro de custo ativo disponível para cadastro (todos já podem estar
                            cadastrados ou a lista não retornou dados).
                        </p>
                    )}
                    <FormComp
                        title="DIAS PRAZO"
                        type="number"
                        valor={diasPrazo}
                        changeFunc={(e) => setDiasPrazo(e.target.value)}
                        required
                    />
                    <FormComp
                        type="submit"
                        valor={submitting ? "SALVANDO…" : "SALVAR"}
                        disabled={submitting || loadingObras || selectOptions.length === 0}
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalNovoCentroCusto;
